var __rest = (this && this.__rest) || function (s, e) {
    var t = {};
    for (var p in s) if (Object.prototype.hasOwnProperty.call(s, p) && e.indexOf(p) < 0)
        t[p] = s[p];
    if (s != null && typeof Object.getOwnPropertySymbols === "function")
        for (var i = 0, p = Object.getOwnPropertySymbols(s); i < p.length; i++) {
            if (e.indexOf(p[i]) < 0 && Object.prototype.propertyIsEnumerable.call(s, p[i]))
                t[p[i]] = s[p[i]];
        }
    return t;
};
import Tippy from "@tippyjs/react";
import { Button, ToggleHorizontalButtons, VerticalButtons } from "components/button";
import { ColorPicker } from "components/colorpicker";
import { ColorWells } from "components/color_wells";
import { ComboSlider } from "components/combo_slider";
import { Icon } from "components/icon";
import { t } from "i18next";
import * as React from "react";
import { useTranslation } from "react-i18next";
import { TierContext } from "tiers/context";
import { Tip } from "view/tooltips";
import { HorizontalIconButtons } from "./button";
import { LabelledCheckbox, LabelledCheckboxGroup } from "./checkbox";
import { Radio } from "./radio";
import { Select } from "./select";
import { TextAreaInput } from "./text_area";
import { TextInput } from "./text_input";
function icon(tabType) {
    switch (tabType) {
        case "text-style": return "paint-roller";
        case "property": return "sliders-simple";
        case "liquid": return "tint";
        case "text": return "i-cursor";
        case "tools": return "wrench";
    }
}
function display(tabType) {
    switch (tabType) {
        case "property": return "Properties";
        case "text-style": return "Style";
        case "liquid": return "Liquid";
        case "text": return "Text";
        case "tools": return "Tools";
    }
}
export function isReactElement(props) {
    return props.spec === undefined;
}
export class PropertyPanel extends React.Component {
    constructor(props) {
        super(props);
        this.ref = React.createRef();
        this.render = () => {
            // If no tabs are specified, render nothing.
            if (this.props.tabs.length === 0)
                return null;
            const currentTabType = this.state.currentTab;
            const currentTab = this.props.tabs.filter(t => t.type === currentTabType)[0];
            const collapsed = this.props.collapsed;
            return React.createElement(TierContext.Consumer, null, tiers => (React.createElement("div", { id: "property-panel", ref: this.ref, "data-simplebar": true },
                React.createElement("div", { className: "content-container" },
                    React.createElement("div", { className: "property-panel-header" + (collapsed ? " collapsed" : "") },
                        React.createElement(Header, { icon: this.props.icon, title: this.props.title }),
                        !collapsed && React.createElement("div", { className: "property-panel-tabs" }, this.props.tabs.map(tab => React.createElement(Tab, Object.assign({ key: tab.type, active: tab.type === this.state.currentTab, onSelect: () => this.setState({ currentTab: tab.type }) }, tab)))),
                        React.createElement(Tip, { content: (collapsed ? "Show" : "Hide") + " panel" },
                            React.createElement("div", { className: "collapse-toggle", onClick: () => this.props.onToggleCollapsed(!collapsed) },
                                React.createElement(Icon, { name: collapsed ? "arrow-down-from-line" : "arrow-up-to-line" })))),
                    !collapsed && this.createElements(currentTab, tiers)))));
        };
        this.state = { currentTab: props.tabs.length > 0 ? props.tabs[0].type : "property" };
    }
    static getDerivedStateFromProps(props, currentState) {
        // Check if the current panel exists in the props. If not, reset to first tab.
        const hasTab = props.tabs.filter(t => t.type === currentState.currentTab).length > 0;
        if (hasTab) {
            return currentState;
        }
        return { currentTab: props.tabs[0].type };
    }
    createElements(tab, tiers) {
        // Flat list of react children.
        var elements = [];
        var isFirstHeader = true;
        for (let section of tab.sections) {
            // Skip sections with no elements, but only if it's not the first header and it
            // does not contain a toggle.
            if (section.elements.length == 0 && !section.toggle)
                continue;
            // Header. Hide uf this is the first section and the section requests to hide it.
            const header = isFirstHeader && section.hideHeaderIfFirst === true
                ? null
                : React.createElement(SectionHeader, Object.assign({ key: `header_${section.header}` }, section));
            // Create the elements.
            let propertyElements = section.elements.map(element => {
                // Special case: if it's a ReactElement, just return it.
                if (isReactElement(element)) {
                    return [element];
                }
                var els = [];
                let key = `label_${element.key}`;
                if (element.spec == "header") {
                    els.push(createElement(element));
                }
                else if (!tiers.allow(element) && !(element.spec === "checkbox-group")) {
                    // Locked item. Show a label with a badge.
                    // Clicking opens a pop up.
                    els.push(React.createElement("div", { className: "flex-group", key: key },
                        React.createElement(PropLabel, { key: key, css: "", label: element.label }),
                        React.createElement("a", { className: "unlock", href: "#", onClick: (e) => { e.preventDefault(); tiers.popup("PP:" + key); } },
                            tiers.badge(element),
                            " ",
                            t("Unlock"))));
                }
                else {
                    let wideClass = (element.spec == "checkbox-group") && element.wideLabel ? "wide" : "";
                    els.push(React.createElement("div", { className: "flex-group", key: key },
                        React.createElement(PropLabel, { key: key, css: wideClass, label: element.label }),
                        createElement(element)));
                }
                return els;
            });
            elements.push(header, ...propertyElements);
            isFirstHeader = false;
        }
        return elements;
    }
}
const createElement = (props) => {
    switch (props.spec) {
        case "slider":
            return React.createElement(ComboSlider, Object.assign({}, props));
        case "radio":
            return React.createElement("div", null,
                React.createElement(Radio, Object.assign({}, props)));
        case "select":
            return React.createElement(Select, Object.assign({}, props));
        case "text":
            return React.createElement(TextInput, Object.assign({}, props));
        case "color":
            return React.createElement(ColorPicker, Object.assign({}, props));
        case "button":
            return React.createElement(Button, Object.assign({}, props));
        case "horizontal-icon-buttons":
            return React.createElement(HorizontalIconButtons, Object.assign({}, props));
        case "toggle-horizontal-buttons":
            return React.createElement(ToggleHorizontalButtons, Object.assign({}, props));
        case "vertical-buttons":
            return React.createElement(VerticalButtons, Object.assign({}, props));
        case "textarea":
            return React.createElement(TextAreaInput, Object.assign({}, props));
        case "checkbox-group":
            return React.createElement(LabelledCheckboxGroup, Object.assign({}, props));
        case "header":
            return React.createElement(SectionHeader, Object.assign({}, props));
        case "color-wells":
            return React.createElement(ColorWells, Object.assign({}, props));
    }
};
const Header = ({ icon, title }) => {
    const { t } = useTranslation();
    const ttitle = typeof title === "string"
        ? t(["ui." + title, "apparatus." + title + ".name", title])
        : title();
    return React.createElement("h2", null,
        React.createElement("i", { className: `fa fa-${icon} mr-hem` }),
        ttitle);
};
/** Property, tab, e.g. "Properties", "Liquid" */
const Tab = (_a) => {
    var { active, onSelect } = _a, tab = __rest(_a, ["active", "onSelect"]);
    const { t } = useTranslation();
    const name = display(tab.type);
    return React.createElement("button", { className: active ? "active" : "", onClick: onSelect },
        React.createElement("div", null,
            React.createElement("i", { className: `fa fa-${icon(tab.type)} mr-1` }),
            t(["ui." + name, name]),
            tab.colorDot ? React.createElement("span", { className: "color-dot", style: { background: tab.colorDot } }) : null));
};
/** Header section, e.g. "Theme" */
const SectionHeader = (props) => {
    const { t } = useTranslation();
    return React.createElement("h3", null,
        React.createElement(Icon, { spacing: true, name: props.icon, fullWidth: true }),
        t(["ui." + props.header, props.header]),
        props.helpArticleId ? React.createElement(Tippy, { content: t("Learn more") },
            React.createElement("a", { className: "info_chip fa fa-question-circle ml-2", "data-beacon-article-sidebar": props.helpArticleId })) : undefined,
        props.toggle ? React.createElement(LabelledCheckbox, Object.assign({ label: "" }, props.toggle, { useSwitch: true })) : undefined);
};
/** Label for a spec, e.g. "Height" */
const PropLabel = ({ css, label }) => {
    const { t } = useTranslation();
    return React.createElement("div", { className: `label ${css}` }, t(["props." + label, "ui." + label, label]));
};
